import { Truck, Download, Calendar, Filter, FileText, FileSpreadsheet, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ResponsiveActionMenu } from "@/components/ui/responsive-action-menu";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface ReportHeaderProps {
  title: string;
  subtitle?: string;
  period?: { start: string; end: string };
  activeFiltersCount?: number;
  onExport?: () => void;
  onExportPDF?: () => void;
  onExportExcel?: () => void;
  onPrint?: () => void;
}

export function ReportHeader({ 
  title, 
  subtitle, 
  period, 
  activeFiltersCount = 0,
  onExport,
  onExportPDF,
  onExportExcel,
  onPrint
}: ReportHeaderProps) {
  const currentDate = format(new Date(), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });

  // If individual export handlers provided, use dropdown
  const hasIndividualHandlers = onExportPDF || onExportExcel || onPrint;

  return (
    <div className="space-y-4 mb-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Card className="p-3 bg-primary/10 border-primary/20">
            <Truck className="h-8 w-8 text-primary" />
          </Card>
          
          <div className="space-y-1">
            <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
            {subtitle && (
              <p className="text-lg text-muted-foreground">{subtitle}</p>
            )}
            
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Badge variant="outline" className="gap-1">
                <Calendar className="h-3 w-3" />
                Gerado em {currentDate}
              </Badge>
              
              {period && period.start && period.end && (
                <Badge variant="outline" className="gap-1">
                  <Calendar className="h-3 w-3" />
                  {(() => {
                    try {
                      const startDate = new Date(period.start);
                      const endDate = new Date(period.end);
                      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                        return null;
                      }
                      return `${format(startDate, "dd/MM/yyyy", { locale: ptBR })} - ${format(endDate, "dd/MM/yyyy", { locale: ptBR })}`;
                    } catch {
                      return null;
                    }
                  })()}
                </Badge>
              )}
              
              {activeFiltersCount > 0 && (
                <Badge variant="secondary" className="gap-1">
                  <Filter className="h-3 w-3" />
                  {activeFiltersCount} {activeFiltersCount === 1 ? 'filtro ativo' : 'filtros ativos'}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {hasIndividualHandlers ? (
          <ResponsiveActionMenu
            trigger={
              <Button className="gap-2">
                <Download className="h-4 w-4" />
                Exportar
              </Button>
            }
            title="Exportar Relatório"
            align="end"
          >
            {onExportPDF && (
              <DropdownMenuItem onClick={onExportPDF}>
                <FileText className="h-4 w-4 mr-2" />
                Exportar PDF
              </DropdownMenuItem>
            )}
            {onExportExcel && (
              <DropdownMenuItem onClick={onExportExcel}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Exportar Excel
              </DropdownMenuItem>
            )}
            {onPrint && (
              <DropdownMenuItem onClick={onPrint}>
                <Printer className="h-4 w-4 mr-2" />
                Imprimir
              </DropdownMenuItem>
            )}
          </ResponsiveActionMenu>
        ) : onExport && (
          <Button onClick={onExport} className="gap-2">
            <Download className="h-4 w-4" />
            Exportar
          </Button>
        )}
      </div>
    </div>
  );
}
